import React, { useRef, useState } from "react";
import GlobalFeedback from "../../global/GlobalFeedback";
import { GlobalButton, GlobalTextField } from "../../global/forms/GlobalFormFields";
import GlobalPaymentButton from "./GlobalPaymentButton";
import { uploadPaymentProof } from "./paymentApi";
import { isActivePhonePePayment } from "./paymentSession";
import {
  formatCategoryLimitLabel,
  useCategoryUploadLimit,
} from "../../utils/imageProcessingLimits";
import "../donor-support/styles/donationPaymentStrip.css";

export default function PaymentStrip({
  paymentType,
  onPaymentTypeChange,
  receiptValue,
  onReceiptChange,
  receiptError,
  amount,
  phonePeCompleted,
  phonePeErrorMsg,
  onPhonePeError,
  formErrorDetails,
  formSubmitting,
  alertId,
  onBeforePayRedirect,
  onClearOnlinePayment,
  receiptMaxLength,
  proofUrl,
  proofFileName,
  onProofChange,
  proofError,
}) {
  const fileInputRef = useRef(null);
  const paymentsMaxBytes = useCategoryUploadLimit("payments");
  const [proofUploading, setProofUploading] = useState(false);
  const [proofUploadError, setProofUploadError] = useState("");

  const isOnline = paymentType === "online";
  const paidOnline = isOnline && isActivePhonePePayment(phonePeCompleted);
  const proofDisabled = formSubmitting || proofUploading;

  const handleProofFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setProofUploadError("");
    setProofUploading(true);
    try {
      const result = await uploadPaymentProof(file);
      onProofChange?.({
        url: result.proof || "",
        fileName: result.file_name || file.name,
      });
    } catch (err) {
      const msg = err?.message || "Upload failed";
      setProofUploadError(msg);
      onProofChange?.({ url: "", fileName: "" });
    } finally {
      setProofUploading(false);
    }
  };

  return (
    <div className="donation-pay-strip">
      <div className="donation-pay-strip__row">
        <span className="donation-pay-strip__label">Payment</span>

        <div className="donation-pay-strip__toggle" role="group" aria-label="Payment method">
          <button
            type="button"
            className={!isOnline ? "is-active" : ""}
            disabled={formSubmitting}
            onClick={() => onPaymentTypeChange("offline")}
          >
            Offline
          </button>
          <button
            type="button"
            className={isOnline ? "is-active" : ""}
            disabled={formSubmitting}
            onClick={() => onPaymentTypeChange("online")}
          >
            Online
          </button>
        </div>

        {!isOnline ? (
          <div className="donation-pay-strip__field">
            <GlobalTextField
              label="Receipt number"
              value={receiptValue}
              onChange={onReceiptChange}
              error={receiptError}
              baseAlertId={alertId}
              placeholder="Receipt no."
              disabled={formSubmitting}
              required
            />
          </div>
        ) : paidOnline ? (
          <div className="donation-pay-strip__paid">
            <span className="donation-pay-strip__badge" title={phonePeCompleted.orderId || ""}>
              Paid · {phonePeCompleted.orderId || "Online"}
            </span>
            {onClearOnlinePayment ? (
              <button
                type="button"
                className="donation-pay-strip__pay-again"
                disabled={formSubmitting}
                onClick={onClearOnlinePayment}
              >
                Pay again
              </button>
            ) : null}
          </div>
        ) : (
          <div className="donation-pay-strip__action">
            <GlobalPaymentButton
              amount={amount}
              label="Pay Now"
              disabled={formSubmitting || !amount}
              onError={onPhonePeError}
              onBeforeRedirect={onBeforePayRedirect}
            />
          </div>
        )}
      </div>

      <div className="donation-pay-strip__proof">
        <span className="donation-pay-strip__label">
          Proof <span className="donation-pay-strip__required">*</span>
        </span>
        <div className="donation-pay-strip__proof-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
            className="donation-pay-strip__file-input"
            disabled={proofDisabled}
            onChange={handleProofFile}
          />
          <GlobalButton
            type="button"
            variant="ghost"
            size="sm"
            disabled={proofDisabled}
            onClick={() => fileInputRef.current?.click()}
          >
            {proofUploading ? "Uploading…" : proofUrl ? "Replace receipt" : "Upload receipt"}
          </GlobalButton>
          {proofUrl ? (
            <>
              <a
                href={proofUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="donation-pay-strip__proof-link"
                title={proofFileName || "View proof"}
              >
                {proofFileName || "View uploaded file"}
              </a>
              <button
                type="button"
                className="donation-pay-strip__proof-clear"
                disabled={proofDisabled}
                onClick={() => {
                  setProofUploadError("");
                  onProofChange?.({ url: "", fileName: "" });
                }}
              >
                Remove
              </button>
            </>
          ) : null}
        </div>
        <p className="donation-pay-strip__proof-hint">
          PDF, JPG, PNG, or WebP — max {formatCategoryLimitLabel(paymentsMaxBytes)}. Required for offline and online.
        </p>
      </div>

      {(formErrorDetails?.phonepe || phonePeErrorMsg) && (
        <div className="donation-pay-strip__errors">
          {formErrorDetails?.phonepe ? (
            <GlobalFeedback type="error" text={formErrorDetails.phonepe} />
          ) : null}
          {phonePeErrorMsg ? <GlobalFeedback type="error" text={phonePeErrorMsg} /> : null}
        </div>
      )}

      {(proofError || proofUploadError) && (
        <div className="donation-pay-strip__errors">
          {proofError ? <GlobalFeedback type="error" text={proofError} /> : null}
          {proofUploadError ? <GlobalFeedback type="error" text={proofUploadError} /> : null}
        </div>
      )}
    </div>
  );
}
